import React from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
} from 'react-native';
import { Text } from '@/components/common/text';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { useUnreadConversationsCount } from '@/hooks/residents/useUnreadConversationsCount';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import type { ThemeContextValue } from '@/hooks/shared/useThemeColors';

// Shared types for profile navigation
export type ProfileNavOption = 'my_profile' | 'saved' | 'inbox';

interface NavOption {
  id: ProfileNavOption;
  title: string;
  subtitle: string;
  accentColor: string;
}

// Shared navigation options data with distinct accent colors
export const NAV_OPTIONS: NavOption[] = [
  { id: 'my_profile', title: 'My profile', subtitle: 'Statistics and listings', accentColor: PRIMITIVE_COLORS.resioBlue },
  { id: 'saved', title: 'Saved', subtitle: 'Items for later', accentColor: PRIMITIVE_COLORS.resioPink },
  { id: 'inbox', title: 'Inbox', subtitle: 'Buying/selling conversations', accentColor: PRIMITIVE_COLORS.resioPurple },
];

interface ProfileNavDropdownProps {
  activeOption: ProfileNavOption;
}

type NavigationProp = StackNavigationProp<RootStackParamList>;

// ============================================================================
// NEW HORIZONTAL TAB IMPLEMENTATION
// ============================================================================

export const ProfileNavDropdown: React.FC<ProfileNavDropdownProps> = ({
  activeOption,
}) => {
  const navigation = useNavigation<NavigationProp>();
  
  // Get unread conversations count for inbox badge
  const { unreadCount } = useUnreadConversationsCount();
  const styles = useThemedStyles(makeStyles);

  const handleOptionPress = (option: ProfileNavOption) => {
    if (option === activeOption) {
      // Already on this screen
      return;
    }

    if (option === 'my_profile') {
      navigation.navigate('MarketplaceProfile');
    } else if (option === 'saved') {
      navigation.navigate('SavedListings');
    } else if (option === 'inbox') {
      navigation.navigate('MarketplaceInbox');
    }
  };

  return (
    <View style={styles.tabContainer}>
      {NAV_OPTIONS.map((option, index) => {
        const isActive = option.id === activeOption;
        const isFirst = index === 0;
        const isLast = index === NAV_OPTIONS.length - 1;
        
        return (
          <TouchableOpacity
            key={option.id}
            style={[
              styles.tabCard,
              isActive && { borderLeftColor: option.accentColor },
              isFirst && styles.tabCardFirst,
              isLast && styles.tabCardLast,
            ]}
            onPress={() => handleOptionPress(option.id)}
            activeOpacity={0.8}
          >
            <View style={styles.tabTextContainer}>
              <View style={styles.titleWithBadge}>
                <Text style={[styles.tabTitle, isActive && styles.tabTitleActive]}>
                  {option.title}
                </Text>
                {option.id === 'inbox' && unreadCount > 0 && (
                  <View style={styles.unreadBadge} />
                )}
              </View>
              <Text style={[styles.tabSubtitle, isActive && styles.tabSubtitleActive]}>
                {option.subtitle}
              </Text>
            </View>
          </TouchableOpacity>
        );
      })}
    </View>
  );
};

const makeStyles = ({ semantic }: ThemeContextValue) => StyleSheet.create({
  // Horizontal Tab Container
  tabContainer: {
    flexDirection: 'row',
    gap: 12,
  },
  // Individual Tab Card
  tabCard: {
    flex: 1,
    backgroundColor: semantic.background.secondary,
    borderRadius: 12,
    paddingVertical: 12,
    paddingHorizontal: 14,
    borderLeftWidth: 3,
    borderLeftColor: 'transparent',
  },
  // Active border color is applied dynamically per tab
  tabCardFirst: {
    // First tab styling if needed
  },
  tabCardLast: {
    // Last tab styling if needed
  },
  tabTextContainer: {
    gap: 2,
  },
  titleWithBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  tabTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: semantic.text.secondary,
    lineHeight: 18,
  },
  tabTitleActive: {
    color: semantic.text.primary,
  },
  tabSubtitle: {
    fontSize: 11,
    fontWeight: '300',
    color: semantic.text.tertiary,
    lineHeight: 15,
  },
  tabSubtitleActive: {
    color: semantic.text.secondary,
  },
  unreadBadge: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#FF7629', // Orange color matching Figma design
  },
});

// ============================================================================
// OLD DROPDOWN IMPLEMENTATION (COMMENTED OUT)
// ============================================================================

/*
import { SVG_COMPONENTS } from '@/assets/admin';

const ChevronDownIcon = SVG_COMPONENTS.CHEVRON_DOWN;
const ChevronUpIcon = SVG_COMPONENTS.CHEVRON_UP;

export const ProfileNavDropdown_OLD: React.FC<ProfileNavDropdownProps> = ({
  activeOption,
}) => {
  const navigation = useNavigation<NavigationProp>();
  const [isOpen, setIsOpen] = useState(false);
  
  // Get unread conversations count for inbox badge
  const { unreadCount } = useUnreadConversationsCount();

  // Get the active option data
  const activeOptionData = NAV_OPTIONS.find(opt => opt.id === activeOption) || NAV_OPTIONS[0];

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleOptionPress = (option: ProfileNavOption) => {
    setIsOpen(false);
    
    if (option === activeOption) {
      // Already on this screen, just close dropdown
      return;
    }

    if (option === 'my_profile') {
      navigation.navigate('MarketplaceProfile');
    } else if (option === 'saved') {
      navigation.navigate('SavedListings');
    } else if (option === 'inbox') {
      navigation.navigate('MarketplaceInbox');
    }
  };

  if (!isOpen) {
    // Closed state - show single header card with chevron-down
    return (
      <TouchableOpacity 
        style={oldStyles.headerCard}
        onPress={handleToggle}
        activeOpacity={0.8}
      >
        <View style={oldStyles.headerContent}>
          <View style={oldStyles.headerTextContainer}>
            <View style={oldStyles.titleWithBadge}>
              <Text style={oldStyles.headerTitle}>{activeOptionData.title}</Text>
              {activeOption === 'inbox' && unreadCount > 0 && (
                <View style={oldStyles.unreadBadge} />
              )}
            </View>
            <Text style={oldStyles.headerSubtitle}>{activeOptionData.subtitle}</Text>
          </View>
          <ChevronDownIcon width={20} height={20} color={MARKETPLACE_COLORS.textMuted} />
        </View>
      </TouchableOpacity>
    );
  }

  // Open state - show dropdown with all options
  return (
    <View style={oldStyles.navDropdownContainer}>
      <View style={oldStyles.navDropdown}>
        {NAV_OPTIONS.map((option, index) => (
          <TouchableOpacity
            key={option.id}
            style={[
              oldStyles.navDropdownItem,
              option.id === activeOption && oldStyles.navDropdownItemActive,
              index === 0 && oldStyles.navDropdownItemFirst,
              index === NAV_OPTIONS.length - 1 && oldStyles.navDropdownItemLast,
            ]}
            onPress={() => handleOptionPress(option.id)}
            activeOpacity={0.8}
          >
            <View style={oldStyles.navDropdownTextContainer}>
              <View style={oldStyles.navDropdownTitleRow}>
                <View style={oldStyles.titleWithBadge}>
                  <Text style={oldStyles.navDropdownTitle}>{option.title}</Text>
                  {option.id === 'inbox' && unreadCount > 0 && (
                    <View style={oldStyles.unreadBadge} />
                  )}
                </View>
                {index === 0 && (
                  <ChevronUpIcon width={20} height={20} color={MARKETPLACE_COLORS.textMuted} />
                )}
              </View>
              <Text style={oldStyles.navDropdownSubtitle}>{option.subtitle}</Text>
            </View>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );
};

const oldStyles = StyleSheet.create({
  // Header Card (closed state)
  headerCard: {
    backgroundColor: MARKETPLACE_COLORS.cardBackground,
    borderRadius: 16,
    borderLeftWidth: 3,
    borderLeftColor: MARKETPLACE_COLORS.accentBlueSolid,
    overflow: 'hidden',
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    paddingVertical: 14,
    paddingLeft: 20,
    paddingRight: 16,
    gap: 17,
  },
  headerTextContainer: {
    flex: 1,
    gap: 4,
  },
  headerTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 21,
  },
  headerSubtitle: {
    fontSize: 12,
    fontWeight: '300',
    color: MARKETPLACE_COLORS.textSecondary,
    lineHeight: 18,
  },
  // Nav Dropdown (open state)
  navDropdownContainer: {
    position: 'relative',
    zIndex: 100,
  },
  navDropdown: {
    backgroundColor: MARKETPLACE_COLORS.cardBackground,
    borderRadius: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.85,
    shadowRadius: 60,
    elevation: 10,
    overflow: 'hidden',
  },
  navDropdownItem: {
    paddingVertical: 14,
    paddingLeft: 20,
    paddingRight: 16,
  },
  navDropdownItemFirst: {
    // First item styling if needed
  },
  navDropdownItemLast: {
    // Last item styling if needed
  },
  navDropdownItemActive: {
    borderLeftWidth: 3,
    borderLeftColor: MARKETPLACE_COLORS.accentBlueSolid,
  },
  navDropdownTextContainer: {
    gap: 4,
  },
  navDropdownTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  titleWithBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  navDropdownTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 21,
  },
  unreadBadge: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#FF7629', // Orange color matching Figma design
  },
  navDropdownSubtitle: {
    fontSize: 12,
    fontWeight: '300',
    color: MARKETPLACE_COLORS.textSecondary,
    lineHeight: 18,
  },
});
*/
